import { useCallback, useEffect, useRef, useState } from 'react';

// Simple debounce function to replace lodash dependency
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  const debouncedFn = (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
  debouncedFn.cancel = () => clearTimeout(timeout);
  return debouncedFn;
};

interface UseFormAutoSaveOptions {
  key: string;
  data: any;
  onSave?: (data: any) => Promise<void>;
  autoSaveInterval?: number;
  debounceMs?: number;
  enabled?: boolean;
}

interface UseFormAutoSaveReturn {
  isSaving: boolean;
  isAutoSaving: boolean;
  lastSaved: Date | null;
  saveNow: () => Promise<void>;
  clearDraft: () => void;
  hasUnsavedChanges: boolean;
}

export const useFormAutoSave = ({
  key,
  data,
  onSave,
  autoSaveInterval = 30000, // 30 seconds
  debounceMs = 1000,
  enabled = true
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const lastDataRef = useRef<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const storageKey = `autosave_${key}`;

  // Save to localStorage
  const saveToLocalStorage = useCallback((saveData: any) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        data: saveData,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }, [storageKey]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback((): any | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.data;
      }
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
    }
    return null;
  }, [storageKey]);

  // Clear localStorage draft
  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    setHasUnsavedChanges(false);
    setLastSaved(null);
  }, [storageKey]);

  // Save function
  const performSave = useCallback(async (saveData: any, isAuto = false) => {
    if (!enabled || !saveData) return;

    if (isAuto) {
      setIsAutoSaving(true);
    } else {
      setIsSaving(true);
    }

    try {
      // Save to localStorage first
      saveToLocalStorage(saveData);
      
      // If onSave callback is provided, call it
      if (onSave) {
        await onSave(saveData);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Keep the data in localStorage even if remote save fails
    } finally {
      setIsSaving(false);
      setIsAutoSaving(false);
    }
  }, [enabled, onSave, saveToLocalStorage]);

  // Debounced save function
  const debouncedSave = useCallback(
    debounce((saveData: any) => {
      performSave(saveData, true);
    }, debounceMs),
    [performSave, debounceMs]
  );

  // Manual save
  const saveNow = useCallback(async () => {
    await performSave(data, false);
  }, [data, performSave]);

  // Check for changes and trigger auto-save
  useEffect(() => {
    if (!enabled || !data) return;

    // Compare with last data to detect changes
    const currentDataString = JSON.stringify(data);
    const lastDataString = JSON.stringify(lastDataRef.current);
    
    if (currentDataString !== lastDataString) {
      lastDataRef.current = data;
      setHasUnsavedChanges(true);
      
      // Trigger debounced save
      debouncedSave(data);
    }
  }, [data, enabled, debouncedSave]);

  // Set up periodic auto-save interval
  useEffect(() => {
    if (!enabled || !autoSaveInterval) return;

    intervalRef.current = setInterval(() => {
      if (hasUnsavedChanges && data) {
        performSave(data, true);
      }
    }, autoSaveInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, autoSaveInterval, hasUnsavedChanges, data, performSave]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return {
    isSaving,
    isAutoSaving,
    lastSaved,
    saveNow,
    clearDraft,
    hasUnsavedChanges
  };
};

// Hook to restore draft data
export const useFormDraftRestore = (key: string) => {
  const storageKey = `autosave_${key}`;
  
  const getDraft = useCallback((): { data: any; timestamp: string } | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to restore draft:', error);
    }
    return null;
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  return { getDraft, clearDraft };
};